import { ReactNode, useState } from 'react';

import * as getStartedCookie from '@cartwhl/common/utils/getStartedCookie';
import Image from '@cartwhl/ui/atoms/Image';
import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

type Slide = 1 | 2 | 3 | 4;
const MAX_SLIDES = 4;

export default function AboutModal({ close }: { close: () => void }) {
  const [slide, setSlide] = useState<Slide>(1);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };

  const next = (skipTo?: Slide) => {
    if (skipTo) {
      setSlide(skipTo);
    } else {
      setSlide((v) => Math.min(v + 1, MAX_SLIDES) as Slide);
    }
  };

  const slides = {
    1: {
      title: 'Welcome to Cartwheel!',
      component: (
        <Slide
          id={1}
          activeSlide={slide}
          mediaURL={`/images/welcome/1.webp`}
          next={next}
          close={close}
          text={
            <>
              Cartwheel is a powerful new way to animate characters for videos,
              games, 3D, movies, ads or social media.
              <br />
              <br />
              We're still early and constantly evolving. We frequently release
              improvements and features weekly. We love feedback, please join
              our{' '}
              <a
                href="https://discord.gg/TQS278evHH"
                target="_blank"
                className="text-blue underline"
              >
                Discord
              </a>
              !
            </>
          }
        />
      ),
    },
    2: {
      title: 'Creating motion in Cartwheel',
      component: (
        <Slide
          id={2}
          activeSlide={slide}
          mediaURL={`https://resources.getcartwheel.com/share/videos/02_creating_motion_448px.mp4`}
          next={next}
          close={close}
          text={
            <>
              You can create motion in Cartwheel by{' '}
              <a
                href="https://docs.getcartwheel.com/docs/creating-motion/video-to-motion"
                target="_blank"
                className="text-blue underline"
              >
                capturing and uploading video
              </a>
              , browsing the{' '}
              <a
                href="https://docs.getcartwheel.com/docs/getting-started/motion-library"
                target="_blank"
                className="text-blue underline"
              >
                Motion Library
              </a>{' '}
              or generate it{' '}
              <a
                href="https://docs.getcartwheel.com/docs/getting-started/prompt-introduction"
                target="_blank"
                className="text-blue underline"
              >
                simply by typing
              </a>
              .
              <br />
              <br />
              You can sequence any number of motions together by using any of
              the methods mentioned above to create smoothly blended
              performances.
            </>
          }
        />
      ),
    },
    3: {
      title: 'Custom characters',
      component: (
        <Slide
          id={3}
          activeSlide={slide}
          mediaURL={`https://resources.getcartwheel.com/share/videos/03_create_characters_448px.mp4`}
          next={next}
          close={close}
          text={
            <>
              <a
                href="https://docs.getcartwheel.com/docs/custom-characters/upload"
                target="_blank"
                className="text-blue underline"
              >
                Upload your own 3D character
              </a>{' '}
              and easily apply Cartwheel motions to them using any of the motion
              creation methods mentioned above.
              <br />
              <br />
              Don't have your own 3D character?{' '}
              <a
                href="https://docs.getcartwheel.com/docs/custom-characters/character-generation"
                target="_blank"
                className="text-blue underline"
              >
                You can create one
              </a>{' '}
              using your own character art or by simply describing what you want
              right in the browser.
            </>
          }
        />
      ),
    },
    4: {
      title: 'Downloading animations',
      component: (
        <Slide
          id={4}
          activeSlide={slide}
          mediaURL={`https://resources.getcartwheel.com/share/videos/04_downloads_448px.mp4`}
          next={next}
          close={close}
          text={
            <>
              You can download your animation in any format allowing you
              seamlessly bring your animations into other software.
              <br />
              <br />
              Downloading for Maya will give you access to our{' '}
              <a
                href="https://docs.getcartwheel.com/docs/integrating-cartwheel/maya-rig"
                target="_blank"
                className="text-blue underline"
              >
                professional rig
              </a>{' '}
              giving you access to IK and FK switching, spline simplification,
              and follow and align spaces. These extended features will make
              editing and refinement even easier.
            </>
          }
        />
      ),
    },
  };

  return (
    <Modal
      title={slides[slide].title}
      open={true}
      onOpenChange={handleOpenChange}
      closeOnClickOutside={false}
    >
      {slides[slide].component}
    </Modal>
  );
}

function Slide({
  id,
  text,
  mediaURL,
  next,
  close,
  activeSlide,
}: {
  id: Slide;
  text: ReactNode;
  mediaURL: string;
  next: (s?: Slide) => void;
  close: () => void;
  activeSlide: number;
}) {
  const closeMe = () => {
    getStartedCookie.remove();
    close();
  };
  return (
    <div>
      {mediaURL.includes('.mp4') ? (
        <Video
          src={mediaURL}
          mode="autoplay"
          className="mx-auto mb-4 aspect-video max-w-[352px] rounded-2xl sm:max-w-[448px]"
        />
      ) : (
        <Image
          src={mediaURL}
          alt={`No alt provided.`}
          className="bg-gray-15 mb-4 block aspect-452/254 w-full rounded-2xl"
        />
      )}
      <Text
        as="p"
        variant="studio/body/md"
        className="min-h-[120px] text-[14px]/[20px]"
      >
        {text}
      </Text>
      <div className="mt-6 flex justify-between">
        <Button onClick={closeMe}>Close</Button>
        <div className="flex items-center gap-2">
          <PageDot
            id={1}
            isActive={activeSlide === 1}
            onClick={() => next(1)}
          />
          <PageDot
            id={2}
            isActive={activeSlide === 2}
            onClick={() => next(2)}
          />
          <PageDot
            id={3}
            isActive={activeSlide === 3}
            onClick={() => next(3)}
          />
          <PageDot
            id={4}
            isActive={activeSlide === 4}
            onClick={() => next(4)}
          />
        </div>
        {activeSlide === MAX_SLIDES ? (
          <Button variant="primary" onClick={closeMe}>
            Finish
          </Button>
        ) : (
          <Button variant="primary" onClick={() => next()}>
            Next →
          </Button>
        )}
      </div>
    </div>
  );
}

function PageDot({
  id,
  onClick,
  isActive,
}: {
  id: number;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={isActive ? `You are on slide ${id}` : `Go to slide ${id}`}
      className={`h-2 w-2 rounded-full border transition-colors ${
        isActive
          ? 'bg-blue border-blue'
          : 'border-gray-45 hover:bg-gray-45 bg-white'
      }`}
    />
  );
}
